/* ==========================================================================
   Home -- the home's own profile, its private job list, and editing both
   ========================================================================== */

import { useState } from 'react';
import { ZipField } from '../../components/ZipField';
import {
  Avatar, Card, Divider, DimBar, Empty, Field, Note, Pick, SearchBox, SectionHead, Slider,
  Stars, Stepper, Switch, matches,
} from '../../components/ui';
import { PhotoStrip, ProfilePhoto } from '../../components/Photos';
import { SigninCode } from '../shared/Misc';
import { HOUSE_RATING_DIMS, JOB_CATALOG, LANGUAGES, LANG_LEVEL } from '../../data/catalog';
import { REVIEWS_OF_HOUSES } from '../../data/seed';
import { job, jobLabel, lang, townLabel, dimLabel } from '../../lib/domain';
import { t } from '../../lib/i18n';
import {
  HOUSE_IDS, activeHouse, addJob, commit, nav, openSheet, removeJob, saveHome, setProperty,
  shareLink, startEditHome, state, toast, toggleIn,
} from '../../store';
import { house } from '../../lib/domain';

/* --------------------------------------------------------------- profile */

export function HouseProfile() {
  const h = activeHouse();
  const revs = REVIEWS_OF_HOUSES[h.id] || [];

  return (
    <div className="screen-pad formgrid">
      {HOUSE_IDS.length > 1 && (
        <Card className="flat">
          <SectionHead title={t('h_yourprops')} />
          <div className="chips">
            {HOUSE_IDS.map((id) => (
              <Pick
                key={id}
                label={house(id).displayName}
                on={state.houseId === id}
                onClick={() => setProperty(id)}
              />
            ))}
          </div>
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => toast('Add a property — same form as your first home')}
          >
            {t('b_add')} another property
          </button>
        </Card>
      )}

      <Card>
        <div className="row top">
          <Avatar p={h} size="lg" />
          <div className="col grow">
            <b className="name" style={{ fontSize: 19 }}>{h.displayName}</b>
            <span className="sub">
              {h.homeType}{townLabel(h) ? ' · ' + townLabel(h) : ''}
            </span>
            <div className="row" style={{ gap: 7, marginTop: 5 }}>
              <Stars value={h.ratings.overall} big />
              <span className="sub tight">{h.ratings.count} cleaners rated you</span>
            </div>
          </div>
        </div>
        <span className="sub">{h.about}</span>
      </Card>

      <Card>
        <SectionHead title={t('h_yourhome')} />
        <div className="chips">
          <span className="chip">{h.bedrooms} bedrooms</span>
          <span className="chip">{h.bathrooms} bathrooms</span>
          <span className="chip">{h.sqft} sq ft</span>
          <span className="chip">{h.floors} floor{h.floors > 1 ? 's' : ''}</span>
          <span className="chip">{h.stairs ? 'Stairs' : 'No stairs'}</span>
          <span className="chip">{h.elevator ? 'Elevator' : 'No elevator'}</span>
          <span className="chip">{h.pets === 'none' ? 'No pets' : h.pets}</span>
          <span className="chip">{h.kids} kids</span>
          <span className="chip">
            {h.suppliesProvided ? 'We provide supplies' : 'Cleaner brings supplies'}
          </span>
          {h.kosherHome && <span className="chip tile">{t('j_kosher')}</span>}
        </div>
      </Card>

      <Card>
        <SectionHead title={t('h_langshere')} />
        <div className="chips">
          {h.languages.map((l) => (
            <span className={'chip' + (l.level === 'basic' ? '' : ' tile')} key={l.id}>
              {lang(l.id)} · {LANG_LEVEL[l.level]}
            </span>
          ))}
        </div>
      </Card>

      <Card>
        <SectionHead title={t('h_howcleanersrate')} />
        {HOUSE_RATING_DIMS.map((d) => (
          <DimBar key={d.id} label={dimLabel(d)} value={(h.ratings as any)[d.id]} />
        ))}
        {h.ratings.ready < 4.2 && (
          <Note warm>
            <b>{t('h_worthknowing')}</b> Your lowest score is on the home being ready to clean.
            Cleaners say clearing surfaces first gets you more done in the same hours.
          </Note>
        )}
      </Card>

      {revs.length > 0 && (
        <Card>
          <SectionHead title={t('h_whatcleanerswrote')} />
          {revs.map((rv, i) => (
            <div key={i}>
              {i > 0 && <Divider />}
              <div className="col" style={{ gap: 5 }}>
                <div className="row between">
                  <span className="row" style={{ gap: 7 }}>
                    <Avatar p={rv.by} size="sm" />
                    <b style={{ fontSize: 13.5 }}>{rv.by.name}</b>
                  </span>
                  <Stars value={rv.overall} />
                </div>
                <span className="sub">&quot;{rv.text}&quot;</span>
              </div>
            </div>
          ))}
        </Card>
      )}

      {/* The way back into this account from another phone. On this screen
          rather than buried in Settings: it is what somebody who has lost
          access comes looking for, and Settings is one level further down
          than a person in that state wants to go. */}
      {!state.me?.isDemo && (
        <Card>
          <SectionHead title={t('h_otherdevice')} />
          <SigninCode />
        </Card>
      )}

      <button type="button" className="btn block" onClick={() => nav('h_joblist')}>
        {t('h_myjoblist')}
      </button>
      <button type="button" className="btn ghost block" onClick={startEditHome}>
        {t('h_edithome')}
      </button>
      {/* The property switcher only appears in the app bar once there are two
          properties, so with one there was no way to reach the sheet and no
          way to get a second. This is that way. */}
      <button
        type="button"
        className="btn ghost block"
        onClick={() => openSheet({ name: 'property' })}
      >
        {HOUSE_IDS.length > 1 ? 'My properties' : 'Add another property'}
      </button>
      {/* Only useful while somebody could answer it, which is also the only
          time the link works. */}
      <button
        type="button"
        className="btn ghost block"
        onClick={() => void shareLink('house', state.houseId)}
      >
        {t('h_sharehome')}
      </button>
      <button type="button" className="btn ghost block" onClick={() => nav('settings')}>
        {t('t_settings')}
      </button>
      <button type="button" className="btn quiet block" onClick={() => nav('feedback')}>
        {t('t_feedback')}
      </button>
    </div>
  );
}

/* -------------------------------------------------------------- job list */

export function JobList() {
  const h = activeHouse();
  const [find, setFind] = useState('');
  const groups = [...new Set(JOB_CATALOG.map((j) => j.group))];
  const have = new Set(h.jobList.map((j) => j.jobId));

  /* Saved jobs grouped the same way the catalog is, so editing the notes on
     every kitchen job means working down one section rather than hunting.

     A job you invented has no catalog group, so it needs a section of its own
     -- without one it saves correctly and then never appears, which looks
     exactly like losing it. */
  const numbered = h.jobList.map((j, i) => ({ ...j, i }));
  const savedByGroup = groups
    .map((g) => ({ group: g, items: numbered.filter((j) => job(j.jobId).group === g) }))
    .filter((g) => g.items.length);

  const ungrouped = numbered.filter((j) => !groups.includes(job(j.jobId).group));
  if (ungrouped.length) savedByGroup.push({ group: 'Your own', items: ungrouped });

  const catalogHits = (g: string) =>
    JOB_CATALOG.filter(
      (j) => j.group === g && !have.has(j.id) && matches(jobLabel(j.id) + ' ' + j.group, find),
    );
  const anyHits = groups.some((g) => catalogHits(g).length);

  return (
    <div className="screen-pad formgrid">
      <Note>
        <b>This is your private list for {h.displayName}.</b> Nobody sees it until you post a
        request and tick which of these you need that time.
      </Note>

      <SectionHead title={t('h_jobsinhome')} action={h.jobList.length + ' saved'} />
      {savedByGroup.map(({ group, items }) => (
        <Card className="flat" key={group}>
          <span
            className="sub tight"
            style={{ fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase' }}
          >
            {group}
          </span>
          {items.map((j, k) => (
            <div key={j.jobId}>
              {k > 0 && <Divider />}
              <div className="col" style={{ gap: 7 }}>
                <b style={{ fontSize: 14.5 }}>{jobLabel(j.jobId)}</b>
                {j.note ? (
                  <span className="sub">{j.note}</span>
                ) : (
                  <span className="hint">
                    {t('h_nonote')}
                  </span>
                )}

                {/* A picture of the thing itself. It belongs to the job on
                    this home's list, not to any one visit, so it is still
                    here the next time somebody is asked to clean it. */}
                <PhotoStrip
                  kind="joblist"
                  ownerId={h.id + ':' + j.jobId}
                  canAdd
                  label={t('h_pictures')}
                />
                <div className="row" style={{ gap: 8 }}>
                  <button
                    type="button"
                    className="btn ghost sm grow"
                    onClick={() => openSheet({ name: 'note', i: j.i })}
                  >
                    {j.note ? t('b_edit') + ' ' + t('note') : 'Add a note'}
                  </button>
                  <button type="button" className="btn quiet sm" onClick={() => removeJob(j.i)}>
                    {t('b_remove')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </Card>
      ))}

      <SectionHead title={t('h_addcatalog')} />
      <SearchBox value={find} onChange={setFind} placeholder={t('h_searchjobs')} />

      <Card className="flat">
        <span className="sub tight" style={{ fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase' }}>
          {t('h_notinlist')}
        </span>
        <span className="hint">
          {t('h_addown_s')}
        </span>
        <button
          type="button"
          className="btn ghost sm"
          onClick={() => openSheet({ name: 'customjob' })}
        >
          {t('b_add')} your own job
        </button>
      </Card>

      {!anyHits && find && (
        <Empty big={t('h_nothingmatches')} sub={t('h_tryword')} />
      )}

      {groups.map((g) => {
        const items = catalogHits(g);
        if (!items.length) return null;
        return (
          <Card className="flat" key={g}>
            <span
              className="sub tight"
              style={{ fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase' }}
            >
              {g}
            </span>
            <div className="chips">
              {items.map((j) => (
                <button
                  key={j.id}
                  type="button"
                  className="pick"
                  onClick={() => addJob(j.id)}
                >
                  + {jobLabel(j.id)}
                </button>
              ))}
            </div>
          </Card>
        );
      })}

      <p className="hint" style={{ textAlign: 'center' }}>
        {t('h_sharedtag')}
      </p>
    </div>
  );
}

/* ---------------------------------------------------------------- editing */

export function EditHome() {
  const e = state.edit;
  /* Saving clears the draft and then goes back, and going back waits for
     the browser -- so for a moment this screen is drawn with no draft. That
     was a crash on every save; now it is a blank moment. */
  if (!e) return null;
  const set = (k: string, v: unknown) => { e[k] = v; commit(); };
  const step = (k: string, d: number) => { e[k] = Math.max(0, e[k] + d); commit(); };

  const h = activeHouse();

  return (
    <div className="screen-pad formgrid">
      <Card>
        <SectionHead title={t('h_picture')} />
        <ProfilePhoto
          kind="profile-house"
          ownerId={h.id}
          current={h.photoId}
          onChanged={(id) => { h.photoId = id; commit(); }}
        />
      </Card>

      <Card>
        <SectionHead title={t('h_basics')} />
        <Field label={t('h_calledas')}>
          <input
            className="input"
            value={e.displayName}
            onChange={(ev) => set('displayName', ev.target.value)}
          />
        </Field>
        <Field label={t('h_hometype')}>
          <div className="chips">
            {['House', 'Apartment', 'Townhouse', 'Brownstone floor'].map((x) => (
              <Pick key={x} label={x} on={e.homeType === x} onClick={() => set('homeType', x)} />
            ))}
          </div>
        </Field>
        <Field
          label={t('h_street')}
          hint="Only released to a cleaner once you have booked them. Nobody browsing sees it."
        >
          <input
            className="input"
            value={e.address ?? ''}
            placeholder="14 Maple Terrace, Apt 2"
            onChange={(ev) => set('address', ev.target.value)}
          />
        </Field>
        <ZipField
          label={t('h_postal')}
          hint="This is what puts you on the map for cleaners nearby. It is not your address — cleaners see the town, never the street, until you book them."
          value={e.zip ?? ''}
          onChange={(z) => set('zip', z)}
        />
        <Field label={t('h_about')}>
          <textarea
            className="textarea"
            value={e.about}
            onChange={(ev) => set('about', ev.target.value)}
          />
        </Field>
      </Card>

      <Card>
        <SectionHead title={t('h_size')} />
        {([
          ['bedrooms', 'Bedrooms'],
          ['bathrooms', 'Bathrooms'],
          ['floors', 'Floors'],
          ['kids', 'Children living here'],
        ] as const).map(([k, label]) => (
          <Field label={label} key={k}>
            <Stepper value={e[k]} onStep={(d) => step(k, d)} />
          </Field>
        ))}
        <Field label={t('h_sqft') + e.sqft}>
          <Slider
            value={e.sqft}
            min={400}
            max={6000}
            step={50}
            onChange={(v) => set('sqft', v)}
            minLabel="400"
            maxLabel="6000"
          />
        </Field>
      </Card>

      <Card>
        <SectionHead title={t('h_practical')} />
        {([
          ['stairs', 'Stairs to climb'],
          ['elevator', 'There is an elevator'],
          ['suppliesProvided', 'We provide the supplies'],
          ['kosherHome', 'Kosher home'],
        ] as const).map(([k, label]) => (
          <div className="switchrow" key={k}>
            <b style={{ fontSize: 14 }}>{label}</b>
            <Switch on={!!e[k]} onClick={() => set(k, !e[k])} />
          </div>
        ))}
        <Field label="Pets">
          <input
            className="input"
            value={e.pets}
            placeholder="none, 1 cat, 2 dogs…"
            onChange={(ev) => set('pets', ev.target.value)}
          />
        </Field>
        <Field label={t('h_parking')}>
          <div className="chips">
            {['Driveway', 'Street', 'Garage', 'None'].map((x) => (
              <Pick key={x} label={x} on={e.parking === x} onClick={() => set('parking', x)} />
            ))}
          </div>
        </Field>
      </Card>

      <Card>
        <SectionHead title={t('h_langshere')} />
        <div className="chips">
          {LANGUAGES.map((l) => (
            <Pick
              key={l.id}
              label={l.label}
              on={e.langIds.includes(l.id)}
              onClick={() => { toggleIn(e.langIds, l.id); commit(); }}
            />
          ))}
        </div>
        <span className="hint">
          {t('h_filteron')}
        </span>
      </Card>

      <button type="button" className="btn block lg" onClick={saveHome}>{t('b_save')}</button>
    </div>
  );
}
